/**
 * Centralized auth environment + SSO referrer derivation.
 *
 * Routing is purely hostname/referrer-driven (no toggles):
 *   - *-localhost.stackline.com → enterprise-sso-localhost + api-localhost
 *   - *-qa.stackline.com        → enterprise-sso (prod)   + api-qa
 *   - *-dev.stackline.com       → enterprise-sso (prod)   + api-dev
 *   - bare *.stackline.com      → enterprise-sso (prod)   + api.stackline.com
 *
 * This matches the pre-existing repo convention in `buildBaseTarget`
 * (src/utils/app.ts) which has always routed any localhost host through
 * api-localhost.stackline.com so V1 session cookies set on the API host
 * are visible across every app subdomain.
 *
 * Working locally therefore implies you have api-localhost.stackline.com
 * resolving to your local API service.
 */

export type AppStage = 'prod' | 'dev' | 'qa' | 'localhost';

export interface ReferrerInfo {
  appName: string;
  stage: AppStage;
  referrer: string;
}

/**
 * Hostname patterns that map to specific apps.
 * Order matters - more specific patterns should come first.
 */
const HOSTNAME_TO_APP: Record<string, string> = {
  'advisor': 'advisor',
  'zenith': 'advisor',
  'atlas': 'atlas',
  'beacon': 'beacon',
  'omni': 'omni',
  'discover': 'discover',
  'drive': 'ad',
  'ad-': 'ad',
  'enterprise-sso': 'enterprise-sso',
};

/**
 * Determine the auth stage from a hostname.
 *   - localhost / *-localhost.stackline.com → 'localhost'
 *   - *-qa.stackline.com → 'qa'
 *   - *-dev/-alpha/-staging/-beta.stackline.com → 'dev'
 *   - bare *.stackline.com → 'prod'
 */
export function getStageFromHostname(hostname: string): AppStage {
  if (hostname.includes('localhost') || hostname.includes('-localhost')) {
    return 'localhost';
  }
  if (/-qa(?:[.-]|$)/.test(hostname)) {
    return 'qa';
  }
  const isProd = hostname.includes('.stackline.com')
    && !/-(?:dev|alpha|staging|beta)/.test(hostname);
  return isProd ? 'prod' : 'dev';
}

/**
 * Extract the app name from a hostname.
 */
export function getAppNameFromHostname(hostname: string): string {
  const lowerHostname = hostname.toLowerCase();

  for (const [pattern, appName] of Object.entries(HOSTNAME_TO_APP)) {
    if (lowerHostname.includes(pattern)) {
      return appName;
    }
  }

  return 'atlas';
}

/**
 * Build the SSO referrer string from app name and stage.
 * Prod referrers have no suffix (e.g. 'atlas'), dev referrers get '-dev'.
 */
export function buildReferrer(appName: string, stage: AppStage): string {
  if (stage === 'prod' || appName === 'enterprise-sso') {
    return appName;
  }
  return `${appName}-${stage}`;
}

/**
 * Get complete referrer info from a hostname.
 */
export function getReferrerInfo(hostname?: string): ReferrerInfo {
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  const appName = getAppNameFromHostname(host);
  const stage = getStageFromHostname(host);
  const referrer = buildReferrer(appName, stage);

  return { appName, stage, referrer };
}

/**
 * Get the SSO referrer string from the current hostname.
 */
export function getSsoReferrer(hostname?: string): string {
  return getReferrerInfo(hostname).referrer;
}

/**
 * Get the enterprise-sso hostname.
 * Local frontends route through enterprise-sso-localhost (served by the same
 * webpack dev server). All other environments use the single shared prod
 * enterprise-sso.stackline.com deployment.
 */
export function getEnterpriseSsoHost(): string {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname.includes('localhost')) return 'enterprise-sso-localhost.stackline.com';
  return 'enterprise-sso.stackline.com';
}

/**
 * Get the Stackline API host for auth V1 + V2 calls.
 *
 * On enterprise-sso the page hostname carries no env signal (the SSO app
 * is the single shared prod deployment, or the local dev server). In that
 * case we derive the stage from the referrer (URL param, then localStorage),
 * which already encodes the originating app's stage (e.g. "atlas-dev"
 * → dev). Falls back to the page hostname for normal app domains.
 */
export function getApiHost(hostname?: string): string {
  let host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');

  if (typeof window !== 'undefined' && host.includes('enterprise-sso')) {
    const referrer = resolveReferrerForApiHost();
    if (referrer) {
      host = `${referrer}.stackline.com`;
    }
  }

  const { stage } = getReferrerInfo(host);
  if (stage === 'localhost') return 'api-localhost.stackline.com';
  if (stage === 'qa') return 'api-qa.stackline.com';
  if (stage === 'dev') return 'api-dev.stackline.com';
  return 'api.stackline.com';
}

function resolveReferrerForApiHost(): string | null {
  try {
    const urlReferrer = new URLSearchParams(window.location.search).get('referrer');
    if (urlReferrer) return urlReferrer;
    return getAuthFlowValue('x-sl-sso-signin-referrer');
  } catch {
    return null;
  }
}

/**
 * Referrers where the hostname doesn't follow the standard {referrer}.stackline.com pattern.
 */
const REFERRER_HOSTNAME_OVERRIDES: Record<string, string> = {
  'advisor-localhost': 'zenith-localhost.stackline.com',
  'gitbook': 'enterprise-help.stackline.com',
  'stackline-support-portal': 'enterprise-help.stackline.com',
};

/**
 * Map referrer prefixes to the hostnames they should redirect to.
 * Used to validate that returnUrl belongs to the correct app.
 */
const REFERRER_TO_VALID_HOSTS: Record<string, string[]> = {
  'advisor': ['zenith', 'advisor'],
  'atlas': ['atlas'],
  'beacon': ['beacon'],
  'omni': ['omni'],
  'discover': ['discover'],
  'ad': ['ad', 'drive'],
  'enterprise-sso': ['enterprise-sso'],
  'gitbook': ['enterprise-help'],
  'stackline-support-portal': ['enterprise-help'],
};

/**
 * Validate that a returnUrl belongs to the same app as the referrer.
 * Prevents cross-app redirects from stale localStorage data.
 */
function isReturnUrlValidForReferrer(referrer: string, returnUrl: string): boolean {
  try {
    const url = new URL(decodeURIComponent(returnUrl));
    const hostname = url.hostname.toLowerCase();

    const referrerBase = referrer.replace(/-(?:localhost|dev|qa|alpha|staging|beta)$/, '');
    const validHosts = REFERRER_TO_VALID_HOSTS[referrerBase] || [referrerBase];

    return validHosts.some(host => hostname.includes(host));
  } catch {
    return false;
  }
}

/**
 * Get the target app URL for a referrer string.
 * Used by enterprise-sso to redirect back to the originating app after login/division-switch.
 */
export function getAppUrlForReferrer(referrer: string, returnUrl?: string): string {
  if (returnUrl && isReturnUrlValidForReferrer(referrer, returnUrl)) {
    return decodeURIComponent(returnUrl);
  }

  if (returnUrl && !isReturnUrlValidForReferrer(referrer, returnUrl)) {
    console.warn('[SSO] Ignoring returnUrl that does not match referrer:', { referrer, returnUrl });
  }

  const baseReferrer = referrer.replace(/-(?:localhost|dev|qa|alpha|staging|beta)$/, '');
  const override = REFERRER_HOSTNAME_OVERRIDES[referrer] || REFERRER_HOSTNAME_OVERRIDES[baseReferrer];
  if (override) {
    return `https://${override}`;
  }

  return `https://${referrer}.stackline.com`;
}

/**
 * Build complete SSO sign-in URL.
 */
export function buildSsoSignInUrl(returnUrl?: string, hostname?: string): string {
  const referrer = getSsoReferrer(hostname);
  const ssoHost = getEnterpriseSsoHost();
  const encodedReturn = returnUrl ? encodeURIComponent(returnUrl) : '';

  return `https://${ssoHost}/sign_in?referrer=${referrer}${encodedReturn ? `&returnUrl=${encodedReturn}` : ''}`;
}

// ---------------------------------------------------------------------------
// TTL-based localStorage helpers for auth flow data (referrer, returnUrl).
// Values expire after 10 minutes to prevent stale redirects.
// ---------------------------------------------------------------------------

const AUTH_FLOW_TTL_MS = 10 * 60 * 1000;

export function setAuthFlowValue(key: string, value: string): void {
  try {
    localStorage.setItem(key, JSON.stringify({ value, ts: Date.now() }));
  } catch { /* quota exceeded or private browsing */ }
}

export function getAuthFlowValue(key: string): string | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed.ts) {
      if (Date.now() - parsed.ts > AUTH_FLOW_TTL_MS) {
        localStorage.removeItem(key);
        return null;
      }
      return parsed.value;
    }
    return raw;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

export function clearAuthFlowData(): void {
  try {
    localStorage.removeItem('x-sl-sso-signin-referrer');
    localStorage.removeItem('x-sl-sso-signin-return-url');
    localStorage.removeItem('x-sl-sso-signin-redirect-url');
  } catch { /* ignore */ }
}
